import { useEffect, useMemo, useState } from 'react'
import { CalendarPlus, Loader2, X } from 'lucide-react'

import { PosCustomerCard, PosCustomerSearch } from '@/components/pos/PosCustomerSearch'
import { BookAppointmentSuccessPanel } from '@/components/calendar/BookAppointmentSuccessPanel'
import { Button } from '@/components/ui/button'
import { createAppointment, fetchAppointmentSources } from '@/lib/api/appointments'
import { fetchServices } from '@/lib/api/services'
import {
  buildBookPayload,
  buildBookableTimeOptions,
  defaultBookTime,
  emptyBookForm,
  findServiceById,
  RECURRENCE_FREQUENCY_OPTIONS,
  validateBookForm,
} from '@/lib/calendar/bookAppointment'

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {string} props.selectedDate
 * @param {string} props.openTime
 * @param {string} props.closeTime
 * @param {number} props.slotMinutes
 * @param {import('@/types/api').Stylist[]} props.stylists
 * @param {(result: { appointments: import('@/types/api').Appointment[] }) => void | Promise<void>} props.onBooked
 */
export function BookAppointmentModal({
  open,
  onClose,
  selectedDate,
  openTime,
  closeTime,
  slotMinutes,
  stylists,
  onBooked,
}) {
  const [form, setForm] = useState(() => emptyBookForm(selectedDate))
  const [services, setServices] = useState([])
  const [sources, setSources] = useState([])
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [errors, setErrors] = useState([])
  const [apiError, setApiError] = useState('')
  const [bookingSuccess, setBookingSuccess] = useState(
    /** @type {{ appointments: import('@/types/api').Appointment[], customer: import('@/types/api').Customer | null } | null} */ (
      null
    ),
  )

  const selectedService = useMemo(
    () => findServiceById(services, form.serviceId),
    [form.serviceId, services],
  )

  const timeOptions = useMemo(
    () =>
      buildBookableTimeOptions(
        openTime,
        closeTime,
        slotMinutes,
        selectedService?.duration ?? 30,
      ),
    [closeTime, openTime, selectedService?.duration, slotMinutes],
  )

  useEffect(() => {
    if (!open) {
      return
    }

    const defaultTime = defaultBookTime(openTime, closeTime, slotMinutes)
    setForm(emptyBookForm(selectedDate, defaultTime))
    setErrors([])
    setApiError('')
    setBookingSuccess(null)

    let active = true

    async function loadOptions() {
      setLoading(true)
      try {
        const [serviceItems, sourceItems] = await Promise.all([
          fetchServices(),
          fetchAppointmentSources(),
        ])
        if (!active) return
        setServices(serviceItems.filter((service) => service.is_active))
        setSources(sourceItems)
      } catch (err) {
        if (!active) return
        setApiError(err?.response?.data?.message ?? 'Unable to load booking options.')
      } finally {
        if (active) setLoading(false)
      }
    }

    loadOptions()

    return () => {
      active = false
    }
  }, [open, selectedDate, openTime, closeTime, slotMinutes])

  useEffect(() => {
    if (!timeOptions.some((option) => option.value === form.time)) {
      setForm((current) => ({
        ...current,
        time: timeOptions[0]?.value ?? current.time,
      }))
    }
  }, [form.time, timeOptions])

  /**
   * @param {import('@/types/api').Customer} customer
   */
  function handleSelectCustomer(customer) {
    setForm((current) => ({
      ...current,
      customer,
      stylistId: customer.preferred_stylist_id
        ? String(customer.preferred_stylist_id)
        : current.stylistId,
    }))
  }

  async function handleSubmit(event) {
    event.preventDefault()

    const validationErrors = validateBookForm(form)
    if (validationErrors.length > 0) {
      setErrors(validationErrors)
      return
    }

    setErrors([])
    setApiError('')
    setSubmitting(true)

    try {
      const result = await createAppointment(buildBookPayload(form))
      await onBooked(result)

      const stylist = form.stylistId
        ? stylists.find((item) => item.id === Number(form.stylistId))
        : null

      const enrichedAppointments = result.appointments.map((appointment, index) => {
        if (index !== 0) {
          return appointment
        }

        return {
          ...appointment,
          customer: appointment.customer ?? (form.customer
            ? {
                id: form.customer.id,
                name: form.customer.name,
                phone: form.customer.phone,
              }
            : null),
          stylist: appointment.stylist ?? (stylist
            ? { id: stylist.id, name: stylist.name }
            : null),
          services: appointment.services?.length
            ? appointment.services
            : (selectedService
              ? [{
                  service_id: selectedService.id,
                  service: { id: selectedService.id, name: selectedService.name },
                }]
              : appointment.services),
        }
      })

      setBookingSuccess({
        appointments: enrichedAppointments,
        customer: form.customer,
      })
    } catch (err) {
      const fieldErrors = err?.response?.data?.errors
      if (fieldErrors) {
        setErrors(
          Object.values(fieldErrors)
            .flat()
            .filter(Boolean),
        )
      } else {
        setApiError(err?.response?.data?.message ?? 'Unable to book appointment.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  if (!open) {
    return null
  }

  const activeStylists = stylists.filter((stylist) => stylist.is_active)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/40 p-4 backdrop-blur-sm">
      <form
        role="dialog"
        aria-modal="true"
        aria-labelledby="book-appointment-title"
        onSubmit={handleSubmit}
        className="card-featured flex max-h-[90vh] w-full max-w-xl flex-col overflow-hidden shadow-luxury-lg"
      >
        <div className="flex items-center justify-between border-b border-primary/10 px-4 py-3">
          <div>
            <p id="book-appointment-title" className="flex items-center gap-2 text-sm font-semibold">
              <CalendarPlus className="h-4 w-4 text-primary" />
              {bookingSuccess ? 'Booking confirmed' : 'Book appointment'}
            </p>
            <p className="text-[11px] text-muted-foreground">
              {bookingSuccess
                ? 'Review the confirmation, then send it on WhatsApp if needed.'
                : `${selectedDate} · ${slotMinutes}-minute slots`}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted/80"
            aria-label="Close booking modal"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {bookingSuccess ? (
          <BookAppointmentSuccessPanel
            appointments={bookingSuccess.appointments}
            customer={bookingSuccess.customer}
            onDone={onClose}
          />
        ) : (
          <>
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading services…
            </div>
          ) : (
            <>
              <section className="space-y-2">
                <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                  Guest
                </p>
                {form.customer ? (
                  <PosCustomerCard
                    customer={form.customer}
                    disabled={submitting}
                    onClear={() => setForm((current) => ({ ...current, customer: null }))}
                  />
                ) : (
                  <PosCustomerSearch
                    disabled={submitting}
                    onSelectCustomer={handleSelectCustomer}
                    error={apiError}
                  />
                )}
              </section>

              <div className="grid gap-3 sm:grid-cols-2">
                <label className="block sm:col-span-2">
                  <span className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                    Service
                  </span>
                  <select
                    required
                    disabled={submitting}
                    value={form.serviceId}
                    onChange={(event) =>
                      setForm((current) => ({ ...current, serviceId: event.target.value }))
                    }
                    className="w-full rounded-xl border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value="">Select service…</option>
                    {services.map((service) => (
                      <option key={service.id} value={service.id}>
                        {service.name} · {service.duration} min
                      </option>
                    ))}
                  </select>
                </label>

                <label className="block">
                  <span className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                    Stylist
                  </span>
                  <select
                    disabled={submitting}
                    value={form.stylistId}
                    onChange={(event) =>
                      setForm((current) => ({ ...current, stylistId: event.target.value }))
                    }
                    className="w-full rounded-xl border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value="">Any / assign later</option>
                    {activeStylists.map((stylist) => (
                      <option key={stylist.id} value={stylist.id}>
                        {stylist.name}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="block">
                  <span className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                    Source
                  </span>
                  <select
                    disabled={submitting}
                    value={form.source}
                    onChange={(event) =>
                      setForm((current) => ({ ...current, source: event.target.value }))
                    }
                    className="w-full rounded-xl border border-input bg-background px-3 py-2 text-sm"
                  >
                    {(sources.length ? sources : [{ value: 'staff', label: 'Staff' }]).map(
                      (source) => (
                        <option key={source.value} value={source.value}>
                          {source.label}
                        </option>
                      ),
                    )}
                  </select>
                </label>

                <label className="block">
                  <span className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                    Date
                  </span>
                  <input
                    type="date"
                    required
                    disabled={submitting}
                    value={form.date}
                    onChange={(event) =>
                      setForm((current) => ({ ...current, date: event.target.value }))
                    }
                    className="w-full rounded-xl border border-input bg-background px-3 py-2 text-sm"
                  />
                </label>

                <label className="block">
                  <span className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                    Time
                  </span>
                  <select
                    required
                    disabled={submitting || timeOptions.length === 0}
                    value={form.time}
                    onChange={(event) =>
                      setForm((current) => ({ ...current, time: event.target.value }))
                    }
                    className="w-full rounded-xl border border-input bg-background px-3 py-2 text-sm"
                  >
                    {timeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <label className="block">
                <span className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                  Notes
                </span>
                <textarea
                  rows={2}
                  disabled={submitting}
                  value={form.notes}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, notes: event.target.value }))
                  }
                  placeholder="Optional booking notes"
                  className="w-full rounded-xl border border-input bg-background px-3 py-2 text-sm"
                />
              </label>

              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  disabled={submitting}
                  checked={form.isBridalEvent}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      isBridalEvent: event.target.checked,
                    }))
                  }
                  className="h-4 w-4 rounded border-input"
                />
                Bridal / event (extended turnover block)
              </label>

              <div className="rounded-xl border border-border/60 bg-muted/20 p-3">
                <label className="flex items-center gap-2 text-sm font-medium">
                  <input
                    type="checkbox"
                    disabled={submitting}
                    checked={form.recurring.enabled}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        recurring: {
                          ...current.recurring,
                          enabled: event.target.checked,
                        },
                      }))
                    }
                    className="h-4 w-4 rounded border-input"
                  />
                  Recurring series
                </label>

                {form.recurring.enabled ? (
                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    <label className="block">
                      <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                        Frequency
                      </span>
                      <select
                        disabled={submitting}
                        value={form.recurring.frequency}
                        onChange={(event) =>
                          setForm((current) => ({
                            ...current,
                            recurring: {
                              ...current.recurring,
                              frequency: event.target.value,
                            },
                          }))
                        }
                        className="w-full rounded-xl border border-input bg-background px-3 py-2 text-sm"
                      >
                        {RECURRENCE_FREQUENCY_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </label>

                    <label className="block">
                      <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                        Occurrences
                      </span>
                      <input
                        type="number"
                        min={2}
                        max={26}
                        disabled={submitting}
                        value={form.recurring.occurrences}
                        onChange={(event) =>
                          setForm((current) => ({
                            ...current,
                            recurring: {
                              ...current.recurring,
                              occurrences: event.target.value,
                            },
                          }))
                        }
                        className="w-full rounded-xl border border-input bg-background px-3 py-2 text-sm"
                      />
                    </label>
                  </div>
                ) : null}
              </div>

              {errors.length > 0 ? (
                <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {errors.map((message) => (
                    <p key={message}>{message}</p>
                  ))}
                </div>
              ) : null}

              {apiError && !form.customer ? null : apiError ? (
                <p className="text-sm text-red-600">{apiError}</p>
              ) : null}
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-primary/10 px-4 py-3">
          <Button type="button" variant="outline" disabled={submitting} onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={submitting || loading}>
            {submitting ? 'Booking…' : 'Book appointment'}
          </Button>
        </div>
          </>
        )}
      </form>
    </div>
  )
}
